import React, { useCallback, useEffect } from "react";
import { useFormik } from "formik";

import {
  Button,
  CustomSelect,
  Error,
  Input,
  Label,
  SelectBadla,
  Switch,
} from "../../Components";
import { badlaOrderSchema } from "../../Schema/BadlaSchema";
import { API_BADLA_LAST_PRICE } from "../../utils/api/ApiConstant";
import { postFetchAxios } from "../../Services/commonService";
import { brokerageTypeOptions, orderTypeOptions } from "../../Static/data";
import { useBadlaInstrumentList } from "../../Hooks";

const findOption = (options, value) =>
  options.find((opt) => opt.value === value) ?? null;

const getOppositeTypeOption = (typeOption) => {
  if (!typeOption?.value) return null;
  return findOption(
    orderTypeOptions,
    typeOption.value === "buy" ? "sell" : "buy",
  );
};

const findInstrumentDetails = (
  instrumentToken,
  allOptions,
  specificOptions,
) => {
  if (!instrumentToken) return { detail: null, instrument: null };

  return {
    detail:
      allOptions.find(
        (x) => x?.instrument_token?.toString() === instrumentToken.toString(),
      ) || null,
    instrument:
      specificOptions.find(
        (x) => x?.value?.toString() === instrumentToken.toString(),
      ) || null,
  };
};

const Rollover = ({ rowData, handleShowHide, onSubmit = () => {} }) => {
  const hasExistingData = !!rowData?._id;

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      badlaType: rowData?.badlaType ?? "",
      settingName: rowData?.settingName ?? "",
      reference: rowData?.reference ?? "",
      badla: null,

      checkBrokrage: rowData?.checkBrokrage ?? false,
      checkRolloverBrokrage: false,

      comexType: rowData?.comexType
        ? findOption(orderTypeOptions, rowData?.comexType)
        : null,
      comexQty: rowData?.comexQty ?? 10,
      comexPrice: rowData?.comexPrice ?? 100,
      comexBroType: rowData?.comexBroType
        ? (findOption(brokerageTypeOptions, rowData?.comexBroType) ??
          brokerageTypeOptions[0])
        : brokerageTypeOptions[0],
      comexBroAmount: rowData?.comexBroAmount ?? 0,
      comexInstrument: null,
      comexDetail: null,
      comexLastBuyPrice: 0,
      comexLastSellPrice: 0,

      mcxType: rowData?.mcxType
        ? findOption(orderTypeOptions, rowData?.mcxType)
        : null,
      mcxQty: rowData?.mcxQty ?? 15,
      mcxPrice: rowData?.mcxPrice ?? 100,
      mcxBroType: rowData?.mcxBroType
        ? (findOption(brokerageTypeOptions, rowData?.mcxBroType) ??
          brokerageTypeOptions[0])
        : brokerageTypeOptions[0],
      mcxBroAmount: rowData?.mcxBroAmount ?? 0,
      mcxInstrument: null,
      mcxDetail: null,
      mcxLastBuyPrice: 0,
      mcxLastSellPrice: 0,

      dgcxType: rowData?.dgcxType
        ? findOption(orderTypeOptions, rowData?.dgcxType)
        : null,
      dgcxQty: rowData?.dgcxQty ?? 13,
      dgcxPrice: rowData?.dgcxPrice ?? 100,
      dgcxBroType: rowData?.dgcxBroType
        ? (findOption(brokerageTypeOptions, rowData?.dgcxBroType) ??
          brokerageTypeOptions[0])
        : brokerageTypeOptions[0],
      dgcxBroAmount: rowData?.dgcxBroAmount ?? 0,
      dgcxInstrument: null,
      dgcxDetail: null,
      dgcxLastBuyPrice: 0,
      dgcxLastSellPrice: 0,

      openingAmount: rowData?.openingAmount ?? "",
      duty: rowData?.duty ?? 0,
      equation: "",
      reverse: "",

      comexRolloverEnabled: false,
      comexRolloverInstrument: null,
      comexRolloverDetail: null,
      comexRolloverType: null,
      comexRolloverQty: rowData?.comexQty ?? 10,
      comexRolloverPrice: "",
      comexRolloverBroType: brokerageTypeOptions[0],
      comexRolloverBroAmount: 0,

      mcxRolloverEnabled: false,
      mcxRolloverInstrument: null,
      mcxRolloverDetail: null,
      mcxRolloverType: null,
      mcxRolloverQty: rowData?.mcxQty ?? 15,
      mcxRolloverPrice: "",
      mcxRolloverBroType: brokerageTypeOptions[0],
      mcxRolloverBroAmount: 0,

      dgcxRolloverEnabled: false,
      dgcxRolloverInstrument: null,
      dgcxRolloverDetail: null,
      dgcxRolloverType: null,
      dgcxRolloverQty: rowData?.dgcxQty ?? 13,
      dgcxRolloverPrice: "",
      dgcxRolloverBroType: brokerageTypeOptions[0],
      dgcxRolloverBroAmount: 0,
    },
    enableReinitialize: true,
    validationSchema: badlaOrderSchema,
    onSubmit: (formValues) => {
      handleFormSubmit(formValues);
    },
  });

  const {
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
    allInstrumentOptions,
  } = useBadlaInstrumentList({ allData: values });

  const comexRolloverInstrumentOptions = comexInstrumentOptions;
  const mcxRolloverInstrumentOptions = mcxInstrumentOptions;
  const dgcxRolloverInstrumentOptions = dgcxInstrumentOptions;

  const fetchLastPrice = async (instrument, fieldPrefix) => {
    if (!instrument?.value) return;

    try {
      const price = await postFetchAxios({
        url: API_BADLA_LAST_PRICE,
        payload: [`${instrument.value}_full`],
      });

      if (price) {
        setFieldValue(`${fieldPrefix}LastBuyPrice`, price?.buy_price_0 ?? 0);
        setFieldValue(`${fieldPrefix}Price`, price?.buy_price_0 ?? 0);
        setFieldValue(`${fieldPrefix}LastSellPrice`, price?.sell_price_0 ?? 0);
      }
    } catch (error) {
      console.error(`Error fetching ${fieldPrefix} price:`, error);
    }
  };

  useEffect(() => {
    fetchLastPrice(values?.comexInstrument, "comex");
  }, [values?.comexInstrument?.value]);

  useEffect(() => {
    fetchLastPrice(values?.mcxInstrument, "mcx");
  }, [values?.mcxInstrument?.value]);

  useEffect(() => {
    fetchLastPrice(values?.dgcxInstrument, "dgcx");
  }, [values?.dgcxInstrument?.value]);

  const handleSelectBadla = useCallback(({ badlaValues }) => {
    setFieldValue("badlaType", badlaValues?.badla?.value);
    setFieldValue("settingName", badlaValues?.matchScript?.settingName);
    setFieldValue("equation", badlaValues?.matchScript?.equation);
    setFieldValue("reverse", badlaValues?.matchScript?.reverse);
  }, []);

  // Initial load from rowData
  useEffect(() => {
    if (!allInstrumentOptions.length || !hasExistingData) return;

    if (!values.comexInstrument && rowData?.comexbadlaInst) {
      const comexData = findInstrumentDetails(
        rowData?.comexbadlaInst,
        allInstrumentOptions,
        comexInstrumentOptions,
      );
      if (comexData.detail) setFieldValue("comexDetail", comexData.detail);
      if (comexData.instrument)
        setFieldValue("comexInstrument", comexData.instrument);
    }

    if (!values.mcxInstrument && rowData?.mcxbadlaInst) {
      const mcxData = findInstrumentDetails(
        rowData?.mcxbadlaInst,
        allInstrumentOptions,
        mcxInstrumentOptions,
      );
      if (mcxData.detail) setFieldValue("mcxDetail", mcxData.detail);
      if (mcxData.instrument)
        setFieldValue("mcxInstrument", mcxData.instrument);
    }

    if (!values.dgcxInstrument && rowData?.dgcxbadlaInst) {
      const dgcxData = findInstrumentDetails(
        rowData?.dgcxbadlaInst,
        allInstrumentOptions,
        dgcxInstrumentOptions,
      );
      if (dgcxData.detail) setFieldValue("dgcxDetail", dgcxData.detail);
      if (dgcxData.instrument)
        setFieldValue("dgcxInstrument", dgcxData.instrument);
    }
  }, [
    hasExistingData,
    rowData,
    allInstrumentOptions,
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
  ]);

  // Detail sync for fresh selection
  useEffect(() => {
    if (!allInstrumentOptions.length) return;

    const comexData = findInstrumentDetails(
      values?.comexInstrument?.value,
      allInstrumentOptions,
      comexInstrumentOptions,
    );
    const mcxData = findInstrumentDetails(
      values?.mcxInstrument?.value,
      allInstrumentOptions,
      mcxInstrumentOptions,
    );
    const dgcxData = findInstrumentDetails(
      values?.dgcxInstrument?.value,
      allInstrumentOptions,
      dgcxInstrumentOptions,
    );

    if (comexData.detail !== values.comexDetail)
      setFieldValue("comexDetail", comexData.detail);
    if (mcxData.detail !== values.mcxDetail)
      setFieldValue("mcxDetail", mcxData.detail);
    if (dgcxData.detail !== values.dgcxDetail)
      setFieldValue("dgcxDetail", dgcxData.detail);
  }, [
    values?.comexInstrument?.value,
    values?.mcxInstrument?.value,
    values?.dgcxInstrument?.value,
    allInstrumentOptions,
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
  ]);

  // Rollover detail sync
  useEffect(() => {
    if (!allInstrumentOptions.length) return;

    const comexData = findInstrumentDetails(
      values?.comexRolloverInstrument?.value,
      allInstrumentOptions,
      comexInstrumentOptions,
    );
    const mcxData = findInstrumentDetails(
      values?.mcxRolloverInstrument?.value,
      allInstrumentOptions,
      mcxInstrumentOptions,
    );
    const dgcxData = findInstrumentDetails(
      values?.dgcxRolloverInstrument?.value,
      allInstrumentOptions,
      dgcxInstrumentOptions,
    );

    if (comexData.detail !== values.comexRolloverDetail)
      setFieldValue("comexRolloverDetail", comexData.detail);
    if (mcxData.detail !== values.mcxRolloverDetail)
      setFieldValue("mcxRolloverDetail", mcxData.detail);
    if (dgcxData.detail !== values.dgcxRolloverDetail)
      setFieldValue("dgcxRolloverDetail", dgcxData.detail);
  }, [
    values?.comexRolloverInstrument?.value,
    values?.mcxRolloverInstrument?.value,
    values?.dgcxRolloverInstrument?.value,
    allInstrumentOptions,
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
  ]);

  // Original behavior
  useEffect(() => {
    if (hasExistingData) return;
    if (values?.comexType) {
      const index = values.comexType.value === "sell" ? 1 : 0;
      setFieldValue("mcxType", orderTypeOptions[index]);
      setFieldValue("dgcxType", orderTypeOptions[index]);
    }
  }, [values?.comexType]);

  // Reset main brokerage
  useEffect(() => {
    const defaultData = values.checkBrokrage ? brokerageTypeOptions[0] : null;
    setFieldValue("mcxBroAmount", 0);
    setFieldValue("mcxBroType", defaultData);
    setFieldValue("comexBroAmount", 0);
    setFieldValue("comexBroType", defaultData);
    setFieldValue("dgcxBroAmount", 0);
    setFieldValue("dgcxBroType", defaultData);
  }, [values.checkBrokrage]);

  // Reset rollover brokerage
  useEffect(() => {
    const defaultData = values.checkRolloverBrokrage
      ? brokerageTypeOptions[0]
      : null;

    setFieldValue("comexRolloverBroAmount", 0);
    setFieldValue("comexRolloverBroType", defaultData);
    setFieldValue("mcxRolloverBroAmount", 0);
    setFieldValue("mcxRolloverBroType", defaultData);
    setFieldValue("dgcxRolloverBroAmount", 0);
    setFieldValue("dgcxRolloverBroType", defaultData);
  }, [values.checkRolloverBrokrage]);

  // Keep rollover type opposite of original type
  useEffect(() => {
    if (values.comexRolloverEnabled) {
      setFieldValue(
        "comexRolloverType",
        getOppositeTypeOption(values.comexType),
      );
    }
  }, [values.comexType?.value, values.comexRolloverEnabled]);

  useEffect(() => {
    if (values.mcxRolloverEnabled) {
      setFieldValue("mcxRolloverType", getOppositeTypeOption(values.mcxType));
    }
  }, [values.mcxType?.value, values.mcxRolloverEnabled]);

  useEffect(() => {
    if (values.dgcxRolloverEnabled) {
      setFieldValue("dgcxRolloverType", getOppositeTypeOption(values.dgcxType));
    }
  }, [values.dgcxType?.value, values.dgcxRolloverEnabled]);

  const resetRolloverFields = useCallback((prefix) => {
    setFieldValue(`${prefix}RolloverInstrument`, null);
    setFieldValue(`${prefix}RolloverDetail`, null);
    setFieldValue(`${prefix}RolloverType`, null);
    setFieldValue(`${prefix}RolloverQty`, "");
    setFieldValue(`${prefix}RolloverPrice`, "");
    setFieldValue(`${prefix}RolloverBroType`, brokerageTypeOptions[0]);
    setFieldValue(`${prefix}RolloverBroAmount`, 0);
  }, []);

  const handleRolloverToggle = useCallback(
    (prefix) => {
      const enabled = values[`${prefix}RolloverEnabled`];
      const nextValue = !enabled;

      setFieldValue(`${prefix}RolloverEnabled`, nextValue);

      if (nextValue) {
        setFieldValue(
          `${prefix}RolloverType`,
          getOppositeTypeOption(values[`${prefix}Type`]),
        );
        setFieldValue(`${prefix}RolloverQty`, values[`${prefix}Qty`] ?? "");
      } else {
        resetRolloverFields(prefix);
      }
    },
    [values],
  );

  const hasAnyRollover =
    values.comexRolloverEnabled ||
    values.mcxRolloverEnabled ||
    values.dgcxRolloverEnabled;

  const handleFormSubmit = useCallback(
    (formValues) => {
      onSubmit("formValues", formValues);
      handleShowHide?.();
    },
    [onSubmit, handleShowHide],
  );

  const renderBaseRow = ({
    prefix,
    label,
    instrumentOptions,
    typeDisabled = false,
  }) => (
    <div className="row m-0 p-0" key={prefix}>
      <div
        className={`${
          values?.checkBrokrage
            ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
            : "col-lg-5 col-sm-6 col-6 m-0 p-1"
        }`}
      >
        <div className="date-input-box">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Label className="lead_text mb-0">{label} Instrument</Label>
            <Switch
              name={`${prefix}RolloverEnabled`}
              onChange={() => handleRolloverToggle(prefix)}
              value={values[`${prefix}RolloverEnabled`]}
              getValue={values[`${prefix}RolloverEnabled`]}
              className="custom-control-input w-auto"
            />
          </div>

          <CustomSelect
            options={instrumentOptions}
            className="dropdown-control"
            name={`${prefix}Instrument`}
            value={values[`${prefix}Instrument`]}
            onChange={(selectedOption) =>
              setFieldValue(`${prefix}Instrument`, selectedOption)
            }
            isClearable={true}
          />
          {errors?.[`${prefix}Instrument`] &&
            touched?.[`${prefix}Instrument`] && (
              <Error>{errors?.[`${prefix}Instrument`]}</Error>
            )}
          <p className="m-0 p-0">
            {values?.[`${prefix}LastBuyPrice`] ?? 0} |{" "}
            {values?.[`${prefix}LastSellPrice`] ?? 0}
          </p>
        </div>
      </div>

      <div
        className={`${
          values?.checkBrokrage
            ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
            : "col-lg-3 col-sm-6 col-6 m-0 p-1"
        }`}
      >
        <div className="date-input-box">
          <Label className="lead_text">Type</Label>
          <CustomSelect
            className="dropdown-control"
            options={orderTypeOptions}
            name={`${prefix}Type`}
            value={values[`${prefix}Type`]}
            onChange={(selectedOption) => {
              setFieldValue(`${prefix}Type`, selectedOption);
            }}
            isDisabled={typeDisabled}
          />
          {errors?.[`${prefix}Type`] && touched?.[`${prefix}Type`] && (
            <Error>{errors?.[`${prefix}Type`]}</Error>
          )}
        </div>
      </div>

      <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
        <div className="form-group">
          <Label className="lead_text">Qty</Label>
          <Input
            type="number"
            placeholder="Qty"
            className="form-control"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[`${prefix}Qty`]}
            id={`${prefix}Qty`}
            name={`${prefix}Qty`}
          />
          {errors?.[`${prefix}Qty`] && touched?.[`${prefix}Qty`] && (
            <Error>{errors?.[`${prefix}Qty`]}</Error>
          )}
        </div>
      </div>

      <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
        <div className="form-group">
          <Label className="lead_text">Price</Label>
          <Input
            type="number"
            placeholder="Price"
            className="form-control"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[`${prefix}Price`]}
            id={`${prefix}Price`}
            name={`${prefix}Price`}
          />
          {errors?.[`${prefix}Price`] && touched?.[`${prefix}Price`] && (
            <Error>{errors?.[`${prefix}Price`]}</Error>
          )}
        </div>
      </div>

      {values?.checkBrokrage && (
        <>
          <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
            <div className="date-input-box">
              <Label className="lead_text">Brokerage Type</Label>
              <CustomSelect
                className="dropdown-control"
                options={brokerageTypeOptions}
                name={`${prefix}BroType`}
                value={values[`${prefix}BroType`]}
                onChange={(selectedOption) =>
                  setFieldValue(`${prefix}BroType`, selectedOption)
                }
                isClearable={true}
              />
            </div>
          </div>

          <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
            <div className="form-group">
              <Label className="lead_text">Brokerage Amount</Label>
              <Input
                type="number"
                placeholder="Brokerage Amount"
                className="form-control"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values[`${prefix}BroAmount`]}
                id={`${prefix}BroAmount`}
                name={`${prefix}BroAmount`}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderRolloverRow = ({ prefix, label, instrumentOptions }) => {
    if (!values[`${prefix}RolloverEnabled`]) return null;

    return (
      <div className="row m-0 p-0" key={`${prefix}-rollover`}>
        <div
          className={`${
            values?.checkRolloverBrokrage
              ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
              : "col-lg-5 col-sm-6 col-6 m-0 p-1"
          }`}
        >
          <div className="date-input-box">
            <Label className="lead_text">{label} Instrument</Label>
            <CustomSelect
              options={instrumentOptions}
              className="dropdown-control"
              name={`${prefix}RolloverInstrument`}
              value={values[`${prefix}RolloverInstrument`]}
              onChange={(selectedOption) =>
                setFieldValue(`${prefix}RolloverInstrument`, selectedOption)
              }
              isClearable={true}
            />
            {errors?.[`${prefix}RolloverInstrument`] &&
              touched?.[`${prefix}RolloverInstrument`] && (
                <Error>{errors?.[`${prefix}RolloverInstrument`]}</Error>
              )}
          </div>
        </div>

        <div
          className={`${
            values?.checkRolloverBrokrage
              ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
              : "col-lg-3 col-sm-6 col-6 m-0 p-1"
          }`}
        >
          <div className="date-input-box">
            <Label className="lead_text">Type</Label>
            <CustomSelect
              className="dropdown-control"
              options={orderTypeOptions}
              name={`${prefix}RolloverType`}
              value={values[`${prefix}RolloverType`]}
              onChange={() => {}}
              isDisabled={true}
            />
          </div>
        </div>

        <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
          <div className="form-group">
            <Label className="lead_text">Qty</Label>
            <Input
              type="number"
              placeholder="Qty"
              className="form-control"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values[`${prefix}RolloverQty`]}
              id={`${prefix}RolloverQty`}
              name={`${prefix}RolloverQty`}
            />
            {errors?.[`${prefix}RolloverQty`] &&
              touched?.[`${prefix}RolloverQty`] && (
                <Error>{errors?.[`${prefix}RolloverQty`]}</Error>
              )}
          </div>
        </div>

        <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
          <div className="form-group">
            <Label className="lead_text">Price</Label>
            <Input
              type="number"
              placeholder="Price"
              className="form-control"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values[`${prefix}RolloverPrice`]}
              id={`${prefix}RolloverPrice`}
              name={`${prefix}RolloverPrice`}
            />
            {errors?.[`${prefix}RolloverPrice`] &&
              touched?.[`${prefix}RolloverPrice`] && (
                <Error>{errors?.[`${prefix}RolloverPrice`]}</Error>
              )}
          </div>
        </div>

        {values?.checkRolloverBrokrage && (
          <>
            <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
              <div className="date-input-box">
                <Label className="lead_text">Brokerage Type</Label>
                <CustomSelect
                  className="dropdown-control"
                  options={brokerageTypeOptions}
                  name={`${prefix}RolloverBroType`}
                  value={values[`${prefix}RolloverBroType`]}
                  onChange={(selectedOption) =>
                    setFieldValue(`${prefix}RolloverBroType`, selectedOption)
                  }
                  isClearable={true}
                />
              </div>
            </div>

            <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
              <div className="form-group">
                <Label className="lead_text">Brokerage Amount</Label>
                <Input
                  type="number"
                  placeholder="Brokerage Amount"
                  className="form-control"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values[`${prefix}RolloverBroAmount`]}
                  id={`${prefix}RolloverBroAmount`}
                  name={`${prefix}RolloverBroAmount`}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <form className="form-order" onSubmit={handleSubmit}>
      <div className="row m-0 p-0 align-items-end">
        <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
          <div className="form-group">
            <Label className="lead_text">Reference Name</Label>
            <Input
              type="text"
              placeholder="Reference Name"
              className="form-control"
              name="reference"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.reference}
              id="reference"
            />
            {errors?.reference && touched?.reference && (
              <Error>{errors?.reference}</Error>
            )}
          </div>
        </div>

        <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
          <div className="form-group">
            <div className="custom-control p-2 w-100 d-flex justify-content-between align-items-center">
              <Label className="lead_text">Add Brokerage</Label>
              <Switch
                name="checkBrokrage"
                onChange={handleChange}
                value={values.checkBrokrage}
                getValue={values.checkBrokrage}
                className="custom-control-input w-auto"
              />
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
          <div className="form-group">
            <Label className="lead_text">Duty</Label>
            <Input
              type="number"
              placeholder="Duty"
              className="form-control"
              name="duty"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.duty}
            />
            {errors?.duty && touched?.duty && <Error>{errors?.duty}</Error>}
          </div>
        </div>

        <SelectBadla
          handleSelectBadla={handleSelectBadla}
          isClearForm={null}
          customClass="col-lg-4"
          badlaType={rowData?.badlaType || values.badlaType}
          settingName={rowData?.settingName || values.settingName}
          isRemoveSelectedBadla={false}
          isShowMatchingBadla={true}
          isDisabled={true}
        />

        {!hasExistingData && (
          <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
            <div className="form-group">
              <Label className="lead_text">Opening Amount(Optional)</Label>
              <Input
                type="number"
                placeholder="Opening Amount"
                className="form-control"
                name="openingAmount"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.openingAmount}
              />
              {errors?.openingAmount && touched?.openingAmount && (
                <Error>{errors?.openingAmount}</Error>
              )}
            </div>
          </div>
        )}

        <div className="col-12 m-0 p-0">
          {renderBaseRow({
            prefix: "comex",
            label: "COMEX",
            instrumentOptions: comexInstrumentOptions,
          })}

          {renderBaseRow({
            prefix: "mcx",
            label: "MCX",
            instrumentOptions: mcxInstrumentOptions,
            typeDisabled: true,
          })}

          {renderBaseRow({
            prefix: "dgcx",
            label: "DGCX",
            instrumentOptions: dgcxInstrumentOptions,
            typeDisabled: true,
          })}
        </div>

        {hasAnyRollover && (
          <div className="col-12 m-0 p-0 mt-3">
            <div className="row m-0 p-0 align-items-center mb-2">
              <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
                <Label className="lead_text mb-0">ROLLOVER INSTRUMENT</Label>
              </div>

              <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
                <div className="form-group mb-0">
                  <div className="custom-control p-2 w-100 d-flex justify-content-between align-items-center">
                    <Label className="lead_text mb-0">Rollover Brokerage</Label>
                    <Switch
                      name="checkRolloverBrokrage"
                      onChange={handleChange}
                      value={values.checkRolloverBrokrage}
                      getValue={values.checkRolloverBrokrage}
                      className="custom-control-input w-auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            {renderRolloverRow({
              prefix: "comex",
              label: "COMEX",
              instrumentOptions: comexRolloverInstrumentOptions,
            })}

            {renderRolloverRow({
              prefix: "mcx",
              label: "MCX",
              instrumentOptions: mcxRolloverInstrumentOptions,
            })}

            {renderRolloverRow({
              prefix: "dgcx",
              label: "DGCX",
              instrumentOptions: dgcxRolloverInstrumentOptions,
            })}
          </div>
        )}
      </div>

      <Button type="submit" id="form-submit-btn" hidden={true}></Button>
    </form>
  );
};

export default Rollover;
